import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import ScheduleShiftModal from "@/components/ScheduleShiftModal";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Pencil, Loader2, Plus, X, Pin, PinOff, FileText,
  Trash2, Phone, Mail, Home as HomeIcon, User, Upload, Download,
  Calendar, DollarSign, ClipboardList, MessageSquare, Clock, FolderOpen,
} from "lucide-react";

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";
const STATUS_STYLE = { active: "bg-emerald-50 text-emerald-700", on_hold: "bg-amber-50 text-amber-700", closed: "bg-gray-100 text-gray-600" };
const URGENCY_STYLE = { crisis: "bg-red-50 text-red-700 border border-red-200", high: "bg-orange-50 text-orange-700", standard: "bg-njas-sage-tint text-njas-sage-hover", low: "bg-gray-50 text-gray-500" };

const TABS = [
  { id: "overview", label: "Overview", icon: User },
  { id: "notes", label: "Case Notes", icon: MessageSquare },
  { id: "contacts", label: "Contact Log", icon: Phone },
  { id: "forms", label: "Forms & Records", icon: ClipboardList },
  { id: "documents", label: "Documents", icon: FolderOpen },
  { id: "calendar", label: "Calendar", icon: Calendar },
  { id: "tasks", label: "Tasks", icon: Calendar },
  { id: "billables", label: "Billables", icon: DollarSign },
  { id: "timeline", label: "Timeline", icon: Clock },
];

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return api.get(`/clients/${id}`).then((r) => setClient(r.data)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const archive = async () => {
    if (!window.confirm("Archive this client? They can be restored from the database if needed.")) return;
    try { await api.delete(`/clients/${id}`); toast.success("Client archived"); nav("/dashboard/clients"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="p-8 text-njas-muted">Loading…</div>;
  if (!client) return <div className="p-8 text-njas-muted">Client not found. <Link to="/dashboard/clients" className="text-njas-navy underline">Back</Link></div>;

  return (
    <div className="fade-in" data-testid="client-detail-page">
      <Link to="/dashboard/clients" className="inline-flex items-center gap-1 text-xs text-njas-muted hover:text-njas-navy mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> All clients
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-njas-sage-tint text-njas-sage-hover flex items-center justify-center font-serif text-2xl">
            {(client.first_name || "?").slice(0, 1)}
          </div>
          <div>
            <div className="eyebrow text-njas-sage-hover text-[10px]">Client</div>
            <h2 className="font-serif text-3xl text-njas-navy">{client.first_name} {client.last_name}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLE[client.status] || "bg-gray-100 text-gray-600"}`}>{(client.status || "active").replace("_", " ")}</span>
              <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${URGENCY_STYLE[client.urgency] || "bg-gray-50 text-gray-500"}`}>{client.urgency}</span>
              {client.assigned_worker_name && <span className="text-xs text-njas-muted">Worker: {client.assigned_worker_name}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditOpen(true)} data-testid="edit-client-btn" className="inline-flex items-center gap-1 rounded-full border border-[var(--njas-border)] bg-white px-4 py-2 text-sm text-njas-navy hover:bg-njas-bg-alt">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
          <button onClick={archive} data-testid="archive-client-btn" className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-white px-4 py-2 text-sm text-red-600 hover:bg-red-50">
            <Trash2 className="w-3.5 h-3.5" /> Archive
          </button>
        </div>
      </div>

      <div className="border-b border-[var(--njas-border)] mb-6 -mx-2 overflow-x-auto">
        <div className="flex gap-1 min-w-max px-2">
          {TABS.map(({ id: t, label, icon: Icon }) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`client-tab-${t}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                tab === t ? "border-njas-navy text-njas-navy" : "border-transparent text-njas-muted hover:text-njas-navy"
              }`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>

      {tab === "overview" && <OverviewTab client={client} reload={load} />}
      {tab === "notes" && <NotesTab client={client} reload={load} />}
      {tab === "contacts" && <ContactLogTab client={client} reload={load} />}
      {tab === "forms" && <FormsTab client={client} reload={load} />}
      {tab === "documents" && <DocumentsTab client={client} reload={load} />}
      {tab === "calendar" && <ClientCalendarTab client={client} />}
      {tab === "tasks" && <TasksTab clientId={client.id} clientName={`${client.first_name} ${client.last_name}`} />}
      {tab === "billables" && <BillablesTab clientId={client.id} />}
      {tab === "timeline" && <TimelineTab clientId={client.id} />}

      {editOpen && <EditClientModal client={client} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />}
    </div>
  );
}

// -------------------- Overview --------------------
function OverviewTab({ client, reload }) {
  return (
    <div className="space-y-6">
      <UpcomingEventsCard client={client} />
      <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card title="Important information" testid="important-info-card">
          {(!client.pinned_submissions || client.pinned_submissions.length === 0) && (
            <div className="text-sm text-njas-muted">
              No pinned forms yet. Go to <strong>Forms &amp; Records</strong>, fill one out, and toggle &quot;Pin to overview&quot; to see it here.
            </div>
          )}
          {client.pinned_submissions?.map((s) => (
            <PinnedSubmissionCard key={s.id} clientId={client.id} sub={s} reload={reload} />
          ))}
        </Card>

        {client.background && (
          <Card title="Background / intake summary">
            <p className="whitespace-pre-wrap text-sm text-njas-text">{client.background}</p>
          </Card>
        )}
      </div>

      <div className="space-y-6">
        <Card title="Contact details">
          <InfoRow icon={Phone} label="Phone" value={client.phone} />
          <InfoRow icon={Mail} label="Email" value={client.email} />
          <InfoRow icon={HomeIcon} label="Address" value={client.address} />
        </Card>
        <Card title="Personal">
          <InfoRow label="Preferred name" value={client.preferred_name} />
          <InfoRow label="Date of birth" value={client.date_of_birth} />
          <InfoRow label="Gender" value={client.gender} />
          <InfoRow label="Pronouns" value={client.pronouns} />
          <InfoRow label="Cultural identity" value={client.cultural_identity} />
        </Card>
        <Card title="Justice">
          <InfoRow label="Status" value={client.justice_status?.replace(/_/g, " ")} />
          <InfoRow label="Tags" value={(client.tags || []).join(", ") || null} />
          <InfoRow label="Source referral" value={client.source_referral_id ? <Link to={`/dashboard/referrals/${client.source_referral_id}`} className="text-njas-navy underline">View referral</Link> : null} />
        </Card>
      </div>
    </div>
    </div>
  );
}

// Upcoming events for the next 14 days — appears at the top of the Overview tab.
function UpcomingEventsCard({ client }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [focused, setFocused] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const end = new Date(today.getTime() + 14 * 86400000);
    const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    try {
      const r = await api.get(`/clients/${client.id}/calendar`, {
        params: { from_date: fmtDate(today), to_date: fmtDate(end) },
      });
      const merged = [];
      (r.data.tasks || []).forEach((t) => {
        const iso = (t.activity_date || t.due_date || "").slice(0, 10);
        if (iso) merged.push({ iso, sortKey: t.activity_date || t.due_date, kind: "task", role: t.created_by_role || "staff", ...t });
      });
      (r.data.shifts || []).forEach((s) => {
        const iso = (s.clock_in || "").slice(0, 10);
        if (iso) merged.push({ iso, sortKey: s.clock_in, kind: "shift", role: s.user_role === "peer_worker" ? "peer_worker" : "staff", ...s });
      });
      (r.data.incidents || []).forEach((i) => {
        const iso = (i.incident_date || "").slice(0, 10);
        if (iso) merged.push({ iso, sortKey: i.incident_date, kind: "incident", role: "incident", ...i });
      });
      (r.data.events || []).forEach((e) => {
        const iso = (e.event_date || "").slice(0, 10);
        if (iso) merged.push({ iso, sortKey: e.event_date, kind: "event", role: e.created_by_role === "peer_worker" ? "peer_worker" : "important", ...e });
      });
      merged.sort((a, b) => (a.sortKey || "").localeCompare(b.sortKey || ""));
      setItems(merged);
    } finally { setLoading(false); }
  }, [client.id]);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const map = new Map();
    items.forEach((ev) => {
      if (!map.has(ev.iso)) map.set(ev.iso, []);
      map.get(ev.iso).push(ev);
    });
    return Array.from(map.entries());
  }, [items]);

  const todayIso = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
  const tomorrowIso = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();

  return (
    <div className="rounded-xl bg-white border border-[var(--njas-border)] p-5" data-testid="upcoming-events-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">Upcoming · Next 2 weeks</div>
          <h3 className="font-serif text-xl text-njas-navy">What&apos;s coming up</h3>
        </div>
        <div className="text-xs text-njas-muted">{items.length} {items.length === 1 ? "item" : "items"}</div>
      </div>

      {loading && <div className="text-sm text-njas-muted">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="text-sm text-njas-muted italic">
          Nothing scheduled in the next 14 days. Add tasks, shifts or important dates from the <strong>Calendar</strong> tab.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-3">
          {grouped.map(([iso, dayEvents]) => {
            const dateObj = new Date(iso + "T00:00:00");
            const dayLabel = iso === todayIso ? "Today"
              : iso === tomorrowIso ? "Tomorrow"
              : dateObj.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
            return (
              <div key={iso} className="flex gap-4">
                <div className={`w-24 shrink-0 text-xs uppercase tracking-wider pt-0.5 ${iso === todayIso ? "text-njas-sage-hover font-semibold" : "text-njas-muted"}`}>{dayLabel}</div>
                <div className="flex-1 flex flex-wrap gap-1.5">
                  {dayEvents.map((ev) => (
                    <button key={`${ev.kind}-${ev.id}`} onClick={() => setFocused(ev)}
                      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-opacity hover:opacity-80 ${EVENT_STYLE[ev.role] || "bg-gray-100 text-gray-700 border-gray-200"}`}
                      data-testid={`upcoming-event-${ev.kind}-${ev.id}`}>
                      <span className="uppercase tracking-wider text-[9px] opacity-70">{ev.kind}</span>
                      <span className="font-medium truncate max-w-[280px]">
                        {ev.kind === "task" ? ev.title
                          : ev.kind === "shift" ? `Shift · ${ev.user_name || ""}`
                          : ev.kind === "incident" ? (ev.incident_type?.replace(/_/g, " ") || "Incident")
                          : ev.title}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {focused && <EventDetailModal ev={focused} clientId={client.id} onClose={() => setFocused(null)} onDeleted={() => { setFocused(null); load(); }} />}
    </div>
  );
}

function PinnedSubmissionCard({ clientId, sub, reload }) {
  const unpin = async () => {
    try { await api.patch(`/clients/${clientId}/submissions/${sub.id}`, { is_pinned: false }); toast.success("Unpinned"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const fields = sub.template_snapshot?.fields || [];
  return (
    <div className="rounded-lg border border-[var(--njas-border)] bg-njas-bg-alt/30 p-4 mb-3">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">{sub.template_category}</div>
          <div className="font-serif text-lg text-njas-navy">{sub.template_name}</div>
          <div className="text-xs text-njas-muted">Updated by {sub.author_name} · {new Date(sub.updated_at).toLocaleDateString("en-AU")}</div>
        </div>
        <button onClick={unpin} className="p-1 text-njas-muted hover:text-njas-navy" title="Unpin"><PinOff className="w-4 h-4" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {fields.map((f) => {
          const v = sub.values?.[f.id];
          if (v === undefined || v === null || v === "" || v === false) return null;
          return (
            <div key={f.id}>
              <div className="text-xs text-njas-muted">{f.label}</div>
              <div className="text-njas-text whitespace-pre-wrap">{typeof v === "boolean" ? "Yes" : String(v)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl bg-white border border-[var(--njas-border)] p-5" data-testid={testid}>
      <div className="eyebrow text-njas-sage-hover text-[10px] mb-3">{title}</div>
      {children}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-1.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-njas-muted mt-1" />}
      <div className="flex-1 min-w-0">
        <div className="text-xs text-njas-muted">{label}</div>
        <div className="text-sm text-njas-text break-words">{value || <span className="text-njas-muted">—</span>}</div>
      </div>
    </div>
  );
}

// -------------------- Notes --------------------
function NotesTab({ client, reload }) {
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("general");
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try { await api.post(`/clients/${client.id}/notes`, { body, category }); setBody(""); toast.success("Note added"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try { await api.delete(`/clients/${client.id}/notes/${noteId}`); toast.success("Note deleted"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const notes = [...(client.notes || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  return (
    <div className="space-y-4">
      <form onSubmit={add} data-testid="add-note-form" className="rounded-xl bg-white border border-[var(--njas-border)] p-5 space-y-3">
        <div className="flex items-center gap-3">
          <span className="eyebrow text-njas-sage-hover text-[10px]">Add case note</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="text-xs border border-[var(--njas-border)] rounded-md px-2 py-1">
            <option value="general">General</option><option value="assessment">Assessment</option><option value="safety">Safety</option>
            <option value="family">Family</option><option value="court">Court</option><option value="welfare">Welfare</option><option value="other">Other</option>
          </select>
        </div>
        <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What happened, what was said, what's next…" className={inputCls} data-testid="add-note-body" />
        <div className="flex justify-end">
          <button type="submit" disabled={busy || !body.trim()} className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-5 py-2 text-sm hover:bg-njas-navy-hover disabled:opacity-60" data-testid="add-note-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save note
          </button>
        </div>
      </form>

      <div className="space-y-3">
        {notes.length === 0 && <div className="text-sm text-njas-muted">No notes yet.</div>}
        {notes.map((n) => (
          <div key={n.id} className="rounded-xl bg-white border border-[var(--njas-border)] p-5" data-testid={`note-${n.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className="inline-block px-2 py-0.5 bg-njas-sage-tint text-njas-sage-hover text-[10px] uppercase tracking-wider rounded">{n.category || "general"}</span>
                <span className="text-xs text-njas-muted ml-2">{n.author_name} · {new Date(n.created_at).toLocaleString("en-AU")}</span>
              </div>
              <button onClick={() => del(n.id)} className="text-xs text-red-600 hover:underline">Delete</button>
            </div>
            <p className="whitespace-pre-wrap text-sm text-njas-text">{n.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------------------- Contact Log --------------------
function ContactLogTab({ client, reload }) {
  const [f, set] = useState({ contact_type: "call", direction: "outbound", contacted_at: new Date().toISOString().slice(0, 16), duration_minutes: "", outcome: "", followup_needed: false, followup_due: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, contacted_at: f.contacted_at ? new Date(f.contacted_at).toISOString() : null, duration_minutes: f.duration_minutes ? Number(f.duration_minutes) : null, followup_due: f.followup_due || null };
      await api.post(`/clients/${client.id}/contacts`, body);
      set({ contact_type: "call", direction: "outbound", contacted_at: new Date().toISOString().slice(0, 16), duration_minutes: "", outcome: "", followup_needed: false, followup_due: "" });
      toast.success("Contact logged"); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (entryId) => {
    if (!window.confirm("Delete this contact log entry?")) return;
    try { await api.delete(`/clients/${client.id}/contacts/${entryId}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const entries = [...(client.contact_log || [])].sort((a, b) => (b.contacted_at || "").localeCompare(a.contacted_at || ""));

  return (
    <div className="space-y-4">
      <form onSubmit={add} data-testid="add-contact-form" className="rounded-xl bg-white border border-[var(--njas-border)] p-5 space-y-3">
        <div className="eyebrow text-njas-sage-hover text-[10px]">Log a contact</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <select value={f.contact_type} onChange={upd("contact_type")} className={inputCls}>
            <option value="call">Call</option><option value="email">Email</option><option value="sms">SMS</option>
            <option value="meeting">Meeting</option><option value="prison_visit">Prison visit</option>
            <option value="home_visit">Home visit</option><option value="video_call">Video call</option>
            <option value="letter">Letter</option><option value="other">Other</option>
          </select>
          <select value={f.direction} onChange={upd("direction")} className={inputCls}>
            <option value="outbound">Outbound (we contacted them)</option>
            <option value="inbound">Inbound (they contacted us)</option>
          </select>
          <input type="datetime-local" value={f.contacted_at} onChange={upd("contacted_at")} className={inputCls} />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <input type="number" min="0" value={f.duration_minutes} onChange={upd("duration_minutes")} placeholder="Duration (minutes)" className={inputCls} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.followup_needed} onChange={upd("followup_needed")} /> Follow-up needed
          </label>
          {f.followup_needed && <input type="date" value={f.followup_due} onChange={upd("followup_due")} className={inputCls} />}
        </div>
        <textarea rows={2} required value={f.outcome} onChange={upd("outcome")} placeholder="Outcome / summary" className={inputCls} />
        <div className="flex justify-end">
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-5 py-2 text-sm hover:bg-njas-navy-hover disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Log contact
          </button>
        </div>
      </form>

      <div className="space-y-3">
        {entries.length === 0 && <div className="text-sm text-njas-muted">No contact entries yet.</div>}
        {entries.map((e) => (
          <div key={e.id} className="rounded-xl bg-white border border-[var(--njas-border)] p-5" data-testid={`contact-${e.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-block px-2 py-0.5 bg-njas-sage-tint text-njas-sage-hover text-[10px] uppercase tracking-wider rounded">{e.contact_type?.replace("_", " ")}</span>
                <span className="text-xs text-njas-muted">{e.direction}</span>
                <span className="text-xs text-njas-muted">· {new Date(e.contacted_at).toLocaleString("en-AU")}</span>
                {e.duration_minutes ? <span className="text-xs text-njas-muted">· {e.duration_minutes} min</span> : null}
                <span className="text-xs text-njas-muted">· {e.author_name}</span>
              </div>
              <button onClick={() => del(e.id)} className="text-xs text-red-600 hover:underline">Delete</button>
            </div>
            <p className="whitespace-pre-wrap text-sm text-njas-text">{e.outcome}</p>
            {e.followup_needed && <div className="mt-2 text-xs text-amber-700">Follow-up due: {e.followup_due || "date TBC"}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// -------------------- Forms & Records --------------------
function FormsTab({ client, reload }) {
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { template, submission? }

  const loadAll = useCallback(async () => {
    const [t, s] = await Promise.all([
      api.get("/form-templates"),
      api.get(`/clients/${client.id}/submissions`),
    ]);
    setTemplates(t.data);
    setSubmissions(s.data);
  }, [client.id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const togglePin = async (sub) => {
    try { await api.patch(`/clients/${client.id}/submissions/${sub.id}`, { is_pinned: !sub.is_pinned }); await loadAll(); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (sub) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/clients/${client.id}/submissions/${sub.id}`); toast.success("Deleted"); await loadAll(); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const byCategory = submissions.reduce((acc, s) => {
    const k = s.template_category || "other";
    (acc[k] = acc[k] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="eyebrow text-njas-sage-hover text-[10px]">Forms & records</div>
        <button onClick={() => setPickerOpen(true)} data-testid="new-form-btn" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-4 py-2 text-sm hover:bg-njas-navy-hover">
          <Plus className="w-4 h-4" /> Fill a form
        </button>
      </div>

      {submissions.length === 0 && <div className="text-sm text-njas-muted">No forms filled out yet. Click &quot;Fill a form&quot; to get started.</div>}

      {Object.entries(byCategory).map(([cat, subs]) => (
        <div key={cat}>
          <div className="text-xs uppercase tracking-wider text-njas-muted font-semibold my-3">{cat}</div>
          <div className="space-y-3">
            {subs.map((s) => (
              <SubmissionCard key={s.id} sub={s} onPin={() => togglePin(s)} onEdit={() => setEditing({ template: null, submission: s })} onDelete={() => del(s)} />
            ))}
          </div>
        </div>
      ))}

      {pickerOpen && (
        <TemplatePicker templates={templates} onPick={(t) => { setPickerOpen(false); setEditing({ template: t }); }} onClose={() => setPickerOpen(false)} />
      )}
      {editing && (
        <FormFillModal clientId={client.id} initialTemplate={editing.template} submission={editing.submission} templates={templates}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadAll(); reload(); }} />
      )}
    </div>
  );
}

function SubmissionCard({ sub, onPin, onEdit, onDelete }) {
  const fields = sub.template_snapshot?.fields || [];
  const shown = fields.filter((f) => {
    const v = sub.values?.[f.id];
    return v !== undefined && v !== null && v !== "" && v !== false;
  }).slice(0, 4);
  return (
    <div className="rounded-xl bg-white border border-[var(--njas-border)] p-5" data-testid={`submission-${sub.id}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="font-serif text-lg text-njas-navy">{sub.template_name}</div>
          <div className="text-xs text-njas-muted">{sub.author_name} · {new Date(sub.updated_at).toLocaleString("en-AU")}</div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onPin} title={sub.is_pinned ? "Unpin from overview" : "Pin to overview"}
            className={`p-1.5 rounded ${sub.is_pinned ? "text-njas-navy bg-njas-sage-tint" : "text-njas-muted hover:text-njas-navy"}`}>
            {sub.is_pinned ? <Pin className="w-4 h-4" /> : <PinOff className="w-4 h-4" />}
          </button>
          <button onClick={onEdit} className="p-1.5 text-njas-muted hover:text-njas-navy" title="Edit"><Pencil className="w-4 h-4" /></button>
          <button onClick={onDelete} className="p-1.5 text-red-500 hover:text-red-700" title="Delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {shown.map((f) => (
          <div key={f.id}>
            <div className="text-xs text-njas-muted">{f.label}</div>
            <div className="text-njas-text whitespace-pre-wrap truncate">{typeof sub.values?.[f.id] === "boolean" ? "Yes" : String(sub.values?.[f.id])}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplatePicker({ templates, onPick, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-serif text-xl text-njas-navy">Pick a form</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-njas-muted" /></button>
        </div>
        <div className="space-y-2">
          {templates.length === 0 && <div className="text-sm text-njas-muted">No templates available. Go to Form Templates in the sidebar to create one.</div>}
          {templates.map((t) => (
            <button key={t.id} onClick={() => onPick(t)} className="w-full text-left rounded-lg border border-[var(--njas-border)] p-3 hover:border-njas-sage hover:bg-njas-bg-alt/40" data-testid={`pick-template-${t.id}`}>
              <div className="font-medium text-njas-navy">{t.name}</div>
              <div className="text-xs text-njas-muted">{t.category} · {t.fields.length} field{t.fields.length === 1 ? "" : "s"}</div>
              {t.description && <div className="text-xs text-njas-muted mt-1">{t.description}</div>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function FormFillModal({ clientId, initialTemplate, submission, templates, onClose, onSaved }) {
  const template = initialTemplate || templates.find((t) => t.id === submission?.template_id);
  const fields = template?.fields || submission?.template_snapshot?.fields || [];
  const [values, setValues] = useState(submission?.values || {});
  const [pinned, setPinned] = useState(submission?.is_pinned || false);
  const [busy, setBusy] = useState(false);

  const upd = (fid, val) => setValues((v) => ({ ...v, [fid]: val }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (submission) {
        await api.patch(`/clients/${clientId}/submissions/${submission.id}`, { values, is_pinned: pinned });
        toast.success("Updated");
      } else {
        await api.post(`/clients/${clientId}/submissions`, { template_id: template.id, values, is_pinned: pinned });
        toast.success("Saved");
      }
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!template) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="eyebrow text-njas-sage-hover text-[10px]">{template.category}</div>
            <h3 className="font-serif text-2xl text-njas-navy">{template.name}</h3>
            {template.description && <div className="text-sm text-njas-muted">{template.description}</div>}
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-njas-muted" /></button>
        </div>
        <form onSubmit={save} className="space-y-4" data-testid="form-fill">
          {fields.map((f) => <FormFieldInput key={f.id} field={f} value={values[f.id]} onChange={(v) => upd(f.id, v)} />)}
          <label className="flex items-center gap-2 text-sm pt-2 border-t border-[var(--njas-border)]">
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="pin-toggle" />
            Pin this record to the client&apos;s <strong>Overview</strong> as important information
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm hover:bg-njas-navy-hover disabled:opacity-60" data-testid="form-fill-submit">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {submission ? "Save changes" : "Save record"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormFieldInput({ field, value, onChange }) {
  const common = { className: inputCls, required: field.required };
  const label = <span className="text-xs font-medium text-njas-navy block mb-1.5 uppercase tracking-wider">{field.label}{field.required && <span className="text-njas-sage ml-1">*</span>}</span>;
  const help = field.help_text ? <div className="text-xs text-njas-muted mt-1">{field.help_text}</div> : null;

  if (field.type === "textarea") return <label className="block">{label}<textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} {...common} />{help}</label>;
  if (field.type === "number") return <label className="block">{label}<input type="number" min={field.min} max={field.max} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} {...common} />{help}</label>;
  if (field.type === "date") return <label className="block">{label}<input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} {...common} />{help}</label>;
  if (field.type === "select") return <label className="block">{label}<select value={value || ""} onChange={(e) => onChange(e.target.value)} {...common}><option value="">—</option>{field.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>{help}</label>;
  if (field.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>{label}
        <div className="flex flex-wrap gap-2">
          {field.options.map((o) => {
            const on = arr.includes(o);
            return <button type="button" key={o} onClick={() => onChange(on ? arr.filter((x) => x !== o) : [...arr, o])} className={`px-3 py-1 rounded-full text-xs border ${on ? "bg-njas-navy text-white border-njas-navy" : "bg-white text-njas-navy border-[var(--njas-border)]"}`}>{o}</button>;
          })}
        </div>{help}</div>
    );
  }
  if (field.type === "checkbox") return <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} /> {field.label}</label>;
  if (field.type === "rating" || field.type === "scale") {
    const min = field.min ?? 1, max = field.max ?? 10;
    return (
      <div>{label}
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
            <button type="button" key={n} onClick={() => onChange(n)} className={`w-9 h-9 rounded border text-sm ${value === n ? "bg-njas-navy text-white border-njas-navy" : "bg-white text-njas-navy border-[var(--njas-border)]"}`}>{n}</button>
          ))}
        </div>{help}</div>
    );
  }
  // default text
  return <label className="block">{label}<input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} {...common} />{help}</label>;
}

// -------------------- Documents --------------------
function DocumentsTab({ client, reload }) {
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("other");
  const fileRef = React.useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    try {
      await api.post(`/clients/${client.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded"); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/clients/${client.id}/documents/${docId}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/clients/${client.id}/documents/${doc.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = doc.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const docs = client.documents || [];
  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-white border border-[var(--njas-border)] p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="eyebrow text-njas-sage-hover text-[10px]">Upload document</div>
            <div className="text-sm text-njas-muted">PDF, images or Word docs. Max 25 MB per file.</div>
          </div>
          <div className="flex items-center gap-2">
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="text-xs border border-[var(--njas-border)] rounded-md px-2 py-1">
              <option value="assessment">Assessment</option><option value="court">Court</option><option value="id">ID</option>
              <option value="letter">Letter</option><option value="consent">Consent</option><option value="medical">Medical</option>
              <option value="other">Other</option>
            </select>
            <label className={`inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-4 py-2 text-sm cursor-pointer hover:bg-njas-navy-hover ${busy ? "opacity-60 pointer-events-none" : ""}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Choose file
              <input ref={fileRef} type="file" onChange={upload} className="hidden" data-testid="upload-file-input" />
            </label>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {docs.length === 0 && <div className="text-sm text-njas-muted">No documents uploaded yet.</div>}
        {docs.map((d) => (
          <div key={d.id} className="rounded-lg bg-white border border-[var(--njas-border)] p-4 flex items-center gap-3" data-testid={`doc-${d.id}`}>
            <FileText className="w-5 h-5 text-njas-muted" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-njas-navy truncate">{d.original_filename}</div>
              <div className="text-xs text-njas-muted">{d.category} · {(d.size_bytes / 1024).toFixed(1)} KB · {d.uploaded_by_name} · {new Date(d.uploaded_at).toLocaleDateString("en-AU")}</div>
            </div>
            <button onClick={() => download(d)} className="text-xs text-njas-navy hover:underline inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> Download</button>
            <button onClick={() => del(d.id)} className="text-xs text-red-600 hover:underline">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------------------- Client Calendar --------------------
const EVENT_STYLE = {
  admin: "bg-blue-100 text-blue-800 border-blue-200",
  staff: "bg-blue-100 text-blue-800 border-blue-200",
  peer_worker: "bg-purple-100 text-purple-800 border-purple-200",
  important: "bg-amber-100 text-amber-800 border-amber-200",
  incident: "bg-red-100 text-red-800 border-red-200",
};
const CAT_STYLE = {
  court: "bg-red-100 text-red-800",
  appointment: "bg-blue-100 text-blue-800",
  birthday: "bg-pink-100 text-pink-800",
  milestone: "bg-emerald-100 text-emerald-800",
  reminder: "bg-yellow-100 text-yellow-800",
  important: "bg-amber-100 text-amber-800",
  other: "bg-gray-100 text-gray-800",
};

function ClientCalendarTab({ client }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [monthAnchor, setMonthAnchor] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d; });
  const [data, setData] = useState({ tasks: [], shifts: [], incidents: [], events: [] });
  const [showAdd, setShowAdd] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [focusedEvent, setFocusedEvent] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(null); // { prefill? }
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const from = fmt(monthAnchor);
    const last = new Date(monthAnchor); last.setMonth(last.getMonth() + 1); last.setDate(0);
    const to = fmt(last);
    try {
      const r = await api.get(`/clients/${client.id}/calendar`, { params: { from_date: from, to_date: to } });
      setData(r.data);
    } finally { setLoading(false); }
  }, [monthAnchor, client.id]);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const map = {};
    const push = (day, ev) => { (map[day] = map[day] || []).push(ev); };
    (data.tasks || []).forEach((t) => {
      const d = (t.activity_date || t.due_date || "").slice(0, 10);
      if (d) push(d, { kind: "task", role: t.created_by_role || "staff", ...t });
    });
    (data.shifts || []).forEach((s) => {
      const d = (s.clock_in || s.scheduled_start || "").slice(0, 10);
      if (d) push(d, { kind: "shift", role: s.user_role === "peer_worker" ? "peer_worker" : "staff", ...s });
    });
    (data.incidents || []).forEach((i) => {
      const d = (i.incident_date || "").slice(0, 10);
      if (d) push(d, { kind: "incident", role: "incident", ...i });
    });
    (data.events || []).forEach((e) => {
      const d = (e.event_date || "").slice(0, 10);
      if (d) push(d, { kind: "event", role: e.created_by_role === "peer_worker" ? "peer_worker" : "important", ...e });
    });
    return map;
  }, [data]);

  const grid = buildCalGrid(monthAnchor);
  const monthLabel = monthAnchor.toLocaleDateString("en-AU", { month: "long", year: "numeric" });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-200" /> Staff / Admin</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-200" /> Peer Worker</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-200" /> Important date</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200" /> Incident</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => setScheduleModal({
                prefill: {
                  client_id: client.id,
                  scheduled_start: `${fmt(new Date())}T09:00`,
                  scheduled_end: `${fmt(new Date())}T13:00`,
                },
              })}
              data-testid="schedule-shift-from-calendar-btn"
              className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-4 py-2 text-sm hover:bg-njas-navy-hover"
            >
              <Plus className="w-4 h-4" /> Schedule shift
            </button>
          )}
          <button onClick={() => { setSelectedDay(fmt(new Date())); setShowAdd(true); }} className="inline-flex items-center gap-2 rounded-full border border-[var(--njas-border)] bg-white text-njas-navy px-4 py-2 text-sm hover:bg-njas-bg-alt" data-testid="add-client-event-btn">
            <Plus className="w-4 h-4" /> Add important date
          </button>
        </div>
      </div>

      <div className="rounded-xl bg-white border border-[var(--njas-border)] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--njas-border)]">
          <button onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1))} className="p-1 rounded hover:bg-njas-bg-alt">‹</button>
          <div className="font-serif text-lg text-njas-navy">{monthLabel}</div>
          <button onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1))} className="p-1 rounded hover:bg-njas-bg-alt">›</button>
        </div>
        <div className="grid grid-cols-7 text-xs text-njas-muted uppercase tracking-wider text-center bg-njas-bg-alt/40 border-b border-[var(--njas-border)]">
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d} className="py-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((cell, i) => {
            const iso = cell ? fmt(cell) : null;
            const events = iso ? (byDay[iso] || []) : [];
            const isToday = iso === fmt(new Date());
            const inMonth = cell && cell.getMonth() === monthAnchor.getMonth();
            return (
              <button key={i} onClick={() => iso && setSelectedDay(iso)} className={`min-h-[90px] border-b border-r border-[var(--njas-border)] p-1.5 text-left align-top transition-colors ${
                !inMonth ? "bg-njas-bg-alt/40 text-njas-muted" : "bg-white hover:bg-njas-bg-alt/40"
              } ${isToday ? "ring-2 ring-njas-sage ring-inset" : ""}`}>
                <div className="text-xs font-medium mb-1">{cell?.getDate()}</div>
                <div className="space-y-0.5" onClick={(e) => e.stopPropagation()}>
                  {events.slice(0, 3).map((ev) => (
                    <div key={`${ev.kind}-${ev.id}`}
                      onClick={(e) => { e.stopPropagation(); setFocusedEvent(ev); }}
                      className={`cursor-pointer text-[10px] px-1.5 py-0.5 rounded border truncate ${EVENT_STYLE[ev.role] || "bg-gray-100 text-gray-700"} hover:opacity-80`}>
                      {ev.kind === "task" ? ev.title : ev.kind === "shift" ? `Shift · ${ev.user_name}` : ev.kind === "incident" ? "Incident" : ev.title}
                    </div>
                  ))}
                  {events.length > 3 && <div className="text-[10px] text-njas-muted">+{events.length - 3}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {loading && <div className="text-xs text-njas-muted">Loading…</div>}

      {selectedDay && (
        <DayModal
          clientId={client.id}
          day={selectedDay}
          events={byDay[selectedDay] || []}
          onClose={() => { setSelectedDay(null); setShowAdd(false); }}
          onSaved={() => { setShowAdd(false); load(); }}
          openAddOnMount={showAdd}
        />
      )}
      {focusedEvent && <EventDetailModal
        ev={focusedEvent}
        clientId={client.id}
        onClose={() => setFocusedEvent(null)}
        onDeleted={() => { setFocusedEvent(null); load(); }}
        onEditShift={(ev) => { setScheduleModal({ mode: "edit", shift: ev }); setFocusedEvent(null); }}
      />}
      {scheduleModal && (
        <ScheduleShiftModal
          mode={scheduleModal.mode || "create"}
          prefill={scheduleModal.prefill}
          shift={scheduleModal.shift}
          lockClient
          onClose={() => setScheduleModal(null)}
          onSaved={() => { setScheduleModal(null); load(); }}
        />
      )}
    </div>
  );
}

function EventDetailModal({ ev, clientId, onClose, onDeleted, onEditShift }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const del = async () => {
    if (ev.kind !== "event") return;
    if (!window.confirm("Delete this important date?")) return;
    try { await api.delete(`/clients/${clientId}/events/${ev.id}`); toast.success("Deleted"); onDeleted(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const goSource = () => {
    if (ev.kind === "shift") nav("/dashboard/shifts");
    else if (ev.kind === "incident") nav("/dashboard/incidents");
    else if (ev.kind === "task") nav("/dashboard/tasks");
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${EVENT_STYLE[ev.role] || "bg-gray-100 text-gray-700"}`}>{ev.kind}</span>
            {ev.kind === "event" && ev.category && <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${CAT_STYLE[ev.category] || "bg-gray-100"}`}>{ev.category}</span>}
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-njas-muted" /></button>
        </div>
        <h3 className="font-serif text-2xl text-njas-navy mb-2">{ev.kind === "task" ? ev.title : ev.kind === "shift" ? (ev.status === "scheduled" ? `Scheduled shift · ${ev.user_name}` : `Shift · ${ev.user_name}`) : ev.kind === "incident" ? `${ev.incident_type?.replace(/_/g, " ")} incident` : ev.title}</h3>

        {ev.kind === "task" && (
          <div className="space-y-2 text-sm">
            {ev.activity_date && <div><span className="text-njas-muted">Date: </span>{new Date(ev.activity_date).toLocaleDateString("en-AU")}</div>}
            {ev.duration_minutes ? <div><span className="text-njas-muted">Duration: </span>{Math.floor(ev.duration_minutes / 60)}h {ev.duration_minutes % 60}m</div> : null}
            {ev.funding_source && <div><span className="text-njas-muted">Funding: </span><span className="capitalize">{ev.funding_source}</span>{ev.funding_details ? ` — ${ev.funding_details}` : ""}</div>}
            {ev.priority && ev.priority !== "normal" && <div><span className="text-njas-muted">Priority: </span><span className="capitalize">{ev.priority}</span></div>}
            {ev.status && <div><span className="text-njas-muted">Status: </span><span className="capitalize">{ev.status.replace("_", " ")}</span></div>}
            {ev.assignee_name && <div><span className="text-njas-muted">Assignee: </span>{ev.assignee_name}</div>}
            {ev.description && <div className="pt-2 whitespace-pre-wrap text-njas-text">{ev.description}</div>}
          </div>
        )}
        {ev.kind === "shift" && (
          <div className="space-y-2 text-sm">
            {ev.scheduled_start && (
              <div><span className="text-njas-muted">Scheduled: </span>{new Date(ev.scheduled_start).toLocaleString("en-AU")} – {new Date(ev.scheduled_end).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}</div>
            )}
            {ev.clock_in && <div><span className="text-njas-muted">Clock in: </span>{new Date(ev.clock_in).toLocaleString("en-AU")}</div>}
            {ev.clock_out && <div><span className="text-njas-muted">Clock out: </span>{new Date(ev.clock_out).toLocaleString("en-AU")}</div>}
            {ev.status === "scheduled" && <div className="text-njas-sage-hover">Awaiting clock-in</div>}
            {ev.status === "active" && <div className="text-emerald-700">Currently active</div>}
            {ev.duration_minutes != null && <div><span className="text-njas-muted">Duration: </span>{Math.floor(ev.duration_minutes / 60)}h {ev.duration_minutes % 60}m</div>}
            {ev.user_name && <div><span className="text-njas-muted">Worker: </span>{ev.user_name} <span className="capitalize text-xs text-njas-muted">({(ev.user_role || "").replace("_", " ")})</span></div>}
            {ev.notes && <div className="pt-2 whitespace-pre-wrap text-njas-text">{ev.notes}</div>}
          </div>
        )}
        {ev.kind === "incident" && (
          <div className="space-y-2 text-sm">
            <div><span className="text-njas-muted">Severity: </span><span className="capitalize">{ev.severity}</span></div>
            <div><span className="text-njas-muted">Status: </span><span className="capitalize">{ev.status}</span></div>
            {ev.location && <div><span className="text-njas-muted">Location: </span>{ev.location}</div>}
            <div><span className="text-njas-muted">Reported by: </span>{ev.reported_by_name} <span className="capitalize text-xs">({(ev.reported_by_role || "").replace("_", " ")})</span></div>
            {ev.description && <div className="pt-2 whitespace-pre-wrap text-njas-text"><strong className="text-njas-navy">What happened:</strong><br/>{ev.description}</div>}
            {ev.actions_taken && <div className="pt-1 whitespace-pre-wrap text-njas-text"><strong className="text-njas-navy">Actions taken:</strong><br/>{ev.actions_taken}</div>}
            {ev.notifications_made?.length > 0 && <div className="text-njas-muted">Notifications: {ev.notifications_made.join(", ")}</div>}
          </div>
        )}
        {ev.kind === "event" && (
          <div className="space-y-2 text-sm">
            <div><span className="text-njas-muted">Date: </span>{new Date(ev.event_date).toLocaleDateString("en-AU")}</div>
            {ev.description && <div className="pt-2 whitespace-pre-wrap text-njas-text">{ev.description}</div>}
            <div className="text-xs text-njas-muted pt-2">Added by {ev.created_by_name}{ev.created_by_role ? ` (${ev.created_by_role.replace("_", " ")})` : ""}</div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-[var(--njas-border)]">
          {ev.kind === "event" && <button onClick={del} className="text-xs text-red-600 hover:underline">Delete</button>}
          {ev.kind === "shift" && isAdmin && ev.status === "scheduled" && onEditShift && (
            <button onClick={() => onEditShift(ev)} data-testid="event-modal-edit-shift-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-njas-navy text-white px-4 py-1.5 text-sm hover:bg-njas-navy-hover">
              <Pencil className="w-3.5 h-3.5" /> Edit shift
            </button>
          )}
          {ev.kind !== "event" && ev.kind !== "shift" && <button onClick={goSource} className="text-xs text-njas-navy hover:underline">Open in list →</button>}
          {ev.kind === "shift" && <button onClick={goSource} className="text-xs text-njas-navy hover:underline">Open in list →</button>}
          <button onClick={onClose} className="rounded-full px-5 py-1.5 text-sm text-njas-muted hover:text-njas-navy">Close</button>
        </div>
      </div>
    </div>
  );
}

function DayModal({ clientId, day, events, onClose, onSaved, openAddOnMount }) {
  const [addOpen, setAddOpen] = useState(openAddOnMount);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-serif text-2xl text-njas-navy">{new Date(day).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-njas-muted" /></button>
        </div>
        <div className="flex justify-end mb-3">
          <button onClick={() => setAddOpen(true)} className="text-sm text-njas-navy hover:underline inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add important date</button>
        </div>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {events.length === 0 && !addOpen && <div className="text-sm text-njas-muted">Nothing on this day.</div>}
          {events.map((ev) => (
            <div key={`${ev.kind}-${ev.id}`} className="rounded-lg border border-[var(--njas-border)] p-3 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${EVENT_STYLE[ev.role] || "bg-gray-100 text-gray-700"}`}>{ev.kind}</span>
                {ev.kind === "event" && <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${CAT_STYLE[ev.category] || "bg-gray-100"}`}>{ev.category}</span>}
                {ev.title && <span className="font-medium">{ev.title}</span>}
              </div>
              {ev.description && <div className="text-njas-text whitespace-pre-wrap">{ev.description}</div>}
              {ev.kind === "task" && ev.duration_minutes ? <div className="text-xs text-njas-muted">Duration: {Math.floor(ev.duration_minutes / 60)}h {ev.duration_minutes % 60}m {ev.funding_source ? `· ${ev.funding_source}` : ""}</div> : null}
              <div className="text-xs text-njas-muted mt-1">
                by {ev.created_by_name || ev.assignee_name || ev.reported_by_name}
                {ev.created_by_role || ev.reported_by_role ? ` (${(ev.created_by_role || ev.reported_by_role || "").replace("_", " ")})` : ""}
              </div>
            </div>
          ))}
        </div>
        {addOpen && <AddEventForm clientId={clientId} initialDate={day} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); onSaved(); }} />}
      </div>
    </div>
  );
}

function AddEventForm({ clientId, initialDate, onClose, onSaved }) {
  const [f, set] = useState({ title: "", event_date: initialDate, category: "important", description: "", all_day: true });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/clients/${clientId}/events`, f); toast.success("Added"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="mt-4 pt-4 border-t border-[var(--njas-border)] space-y-3" data-testid="add-event-form">
      <input required placeholder="Title (e.g. Court hearing)" value={f.title} onChange={upd("title")} className={inputCls} data-testid="event-title" />
      <div className="grid grid-cols-2 gap-3">
        <input required type="date" value={f.event_date} onChange={upd("event_date")} className={inputCls} />
        <select value={f.category} onChange={upd("category")} className={inputCls}>
          <option value="important">Important</option>
          <option value="court">Court</option>
          <option value="appointment">Appointment</option>
          <option value="birthday">Birthday</option>
          <option value="milestone">Milestone</option>
          <option value="reminder">Reminder</option>
          <option value="other">Other</option>
        </select>
      </div>
      <textarea rows={2} value={f.description} onChange={upd("description")} placeholder="Description (optional)" className={inputCls} />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded-full px-4 py-1.5 text-sm text-njas-muted">Cancel</button>
        <button type="submit" disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-njas-navy text-white px-5 py-1.5 text-sm hover:bg-njas-navy-hover disabled:opacity-60" data-testid="event-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
        </button>
      </div>
    </form>
  );
}

function fmt(d) {
  if (!d) return "";
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function buildCalGrid(anchor) {
  const first = new Date(anchor);
  const startWeekday = (first.getDay() + 6) % 7;
  const cells = [];
  const DAY = 86400000;
  for (let i = 0; i < startWeekday; i++) cells.push(new Date(first.getTime() - (startWeekday - i) * DAY));
  const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  for (let d = 1; d <= monthEnd.getDate(); d++) cells.push(new Date(anchor.getFullYear(), anchor.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(new Date(cells[cells.length - 1].getTime() + DAY));
  return cells;
}

// -------------------- Tasks (per-client) --------------------
function TasksTab({ clientId, clientName }) {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(() => api.get("/tasks", { params: { client_id: clientId } }).then((r) => setRows(r.data)), [clientId]);
  useEffect(() => { load(); api.get("/staff/list").then((r) => setStaff(r.data)); }, [load]);

  return <TasksInner rows={rows} reload={load} staff={staff} clientId={clientId} clientName={clientName} showNewInit={showNew} setShowNew={setShowNew} />;
}

function TasksInner({ rows, reload, staff, clientId, clientName, showNewInit, setShowNew }) {
  const [editing, setEditing] = useState(null);
  const upd = async (id, patch) => {
    try { await api.patch(`/tasks/${id}`, patch); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete task?")) return;
    try { await api.delete(`/tasks/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-4 py-2 text-sm hover:bg-njas-navy-hover" data-testid="new-task-btn">
          <Plus className="w-4 h-4" /> New task
        </button>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-sm text-njas-muted">No tasks yet.</div>}
        {rows.map((t) => (
          <div key={t.id} className="rounded-lg bg-white border border-[var(--njas-border)] p-4 flex items-center gap-3" data-testid={`task-${t.id}`}>
            <input type="checkbox" checked={t.status === "done"} onChange={(e) => upd(t.id, { status: e.target.checked ? "done" : "open" })} />
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium ${t.status === "done" ? "text-njas-muted line-through" : "text-njas-navy"}`}>{t.title}</div>
              <div className="text-xs text-njas-muted">
                {t.priority !== "normal" && <span className="capitalize mr-2">{t.priority}</span>}
                {t.due_date && <span>Due {new Date(t.due_date).toLocaleDateString("en-AU")} · </span>}
                {t.assignee_name || "Unassigned"}
                {t.client_name && clientId == null && <span> · {t.client_name}</span>}
              </div>
              {t.description && <div className="text-xs text-njas-muted mt-1">{t.description}</div>}
            </div>
            <button onClick={() => setEditing(t)} className="text-xs text-njas-navy hover:underline">Edit</button>
            <button onClick={() => del(t.id)} className="text-xs text-red-600 hover:underline">Delete</button>
          </div>
        ))}
      </div>
      {showNewInit && <TaskModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); reload(); }} staff={staff} initial={{ client_id: clientId, client_name: clientName }} />}
      {editing && <TaskModal onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} staff={staff} initial={editing} />}
    </div>
  );
}

function TaskModal({ onClose, onSaved, staff, initial }) {
  const [f, set] = useState({
    title: initial?.title || "", description: initial?.description || "",
    due_date: initial?.due_date || "", priority: initial?.priority || "normal",
    assignee_id: initial?.assignee_id || "", client_id: initial?.client_id || "", status: initial?.status || "open",
    duration_minutes: initial?.duration_minutes || "",
    funding_source: initial?.funding_source || "",
    funding_details: initial?.funding_details || "",
    activity_date: initial?.activity_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...f,
        due_date: f.due_date || null,
        assignee_id: f.assignee_id || null,
        client_id: f.client_id || null,
        duration_minutes: f.duration_minutes === "" ? null : Number(f.duration_minutes),
        funding_source: f.funding_source || null,
        funding_details: f.funding_details || null,
        activity_date: f.activity_date || null,
      };
      if (initial?.id) await api.patch(`/tasks/${initial.id}`, body);
      else await api.post("/tasks", body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-xl text-njas-navy mb-4">{initial?.id ? "Edit task" : "New task"}</h3>
        <form onSubmit={save} className="space-y-3" data-testid="task-form">
          <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Title</span><input required value={f.title} onChange={upd("title")} className={inputCls} data-testid="task-title" /></label>
          <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Description / what was done</span><textarea rows={2} value={f.description} onChange={upd("description")} className={inputCls} /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Activity date (when work was done)</span><input type="date" value={f.activity_date} onChange={upd("activity_date")} className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Duration (mins)</span><input type="number" min="0" value={f.duration_minutes} onChange={upd("duration_minutes")} placeholder="e.g. 60" className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Funding source</span>
              <select value={f.funding_source} onChange={upd("funding_source")} className={inputCls}>
                <option value="">—</option><option value="medicare">Medicare</option><option value="ndis">NDIS</option>
                <option value="grant">Grant</option><option value="private">Private</option><option value="pro_bono">Pro bono</option><option value="other">Other</option>
              </select>
            </label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Funding details</span><input value={f.funding_details} onChange={upd("funding_details")} placeholder="e.g. grant name, program" className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Due date</span><input type="date" value={f.due_date} onChange={upd("due_date")} className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Priority</span>
              <select value={f.priority} onChange={upd("priority")} className={inputCls}>
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
            </label>
            <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-njas-navy">Assignee</span>
              <select value={f.assignee_id} onChange={upd("assignee_id")} className={inputCls}>
                <option value="">Unassigned</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-njas-navy">Status</span>
              <select value={f.status} onChange={upd("status")} className={inputCls}>
                <option value="open">Open</option><option value="in_progress">In progress</option><option value="done">Done</option><option value="cancelled">Cancelled</option>
              </select>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm hover:bg-njas-navy-hover disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------- Billables --------------------
function BillablesTab({ clientId }) {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/billables", { params: { client_id: clientId } }).then((r) => setRows(r.data)), [clientId]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this billable?")) return;
    try { await api.delete(`/billables/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const total = rows.reduce((s, r) => s + (r.total_amount || 0), 0);
  const unbilled = rows.filter((r) => r.status === "unbilled").reduce((s, r) => s + (r.total_amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="grid grid-cols-2 gap-6 text-sm">
          <div><div className="text-xs text-njas-muted">Total billed</div><div className="font-serif text-2xl text-njas-navy">${total.toFixed(2)}</div></div>
          <div><div className="text-xs text-njas-muted">Unbilled</div><div className="font-serif text-2xl text-amber-700">${unbilled.toFixed(2)}</div></div>
        </div>
        <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-4 py-2 text-sm hover:bg-njas-navy-hover" data-testid="new-billable-btn">
          <Plus className="w-4 h-4" /> New billable
        </button>
      </div>
      <div className="rounded-xl bg-white border border-[var(--njas-border)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-njas-bg-alt text-njas-muted text-xs uppercase tracking-wider">
            <tr><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">MBS</th><th className="px-4 py-2 text-left">Service</th><th className="px-4 py-2 text-right">Duration</th><th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2 text-left">Funding</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-njas-muted">No billables yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[var(--njas-border)]">
                <td className="px-4 py-2">{r.service_date ? new Date(r.service_date).toLocaleDateString("en-AU") : "—"}</td>
                <td className="px-4 py-2 text-njas-muted">{r.mbs_item || "—"}</td>
                <td className="px-4 py-2">{r.service_description}</td>
                <td className="px-4 py-2 text-right">{r.duration_minutes} min</td>
                <td className="px-4 py-2 text-right">${(r.total_amount || 0).toFixed(2)}</td>
                <td className="px-4 py-2 capitalize">{r.funding_source}</td>
                <td className="px-4 py-2 capitalize">{r.status?.replace("_", " ")}</td>
                <td className="px-4 py-2 text-right space-x-2">
                  <button onClick={() => setEditing(r)} className="text-xs text-njas-navy hover:underline">Edit</button>
                  <button onClick={() => del(r.id)} className="text-xs text-red-600 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showNew && <BillableModal clientId={clientId} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editing && <BillableModal clientId={clientId} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function BillableModal({ clientId, initial, onClose, onSaved }) {
  const [f, set] = useState({
    service_date: initial?.service_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
    mbs_item: initial?.mbs_item || "",
    service_description: initial?.service_description || "",
    duration_minutes: initial?.duration_minutes || 60,
    unit_amount: initial?.unit_amount || "",
    total_amount: initial?.total_amount || "",
    funding_source: initial?.funding_source || "medicare",
    invoice_number: initial?.invoice_number || "",
    status: initial?.status || "unbilled",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...f,
        client_id: clientId,
        duration_minutes: Number(f.duration_minutes),
        unit_amount: f.unit_amount === "" ? null : Number(f.unit_amount),
        total_amount: Number(f.total_amount),
      };
      if (initial?.id) await api.patch(`/billables/${initial.id}`, body);
      else await api.post("/billables", body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-xl text-njas-navy mb-4">{initial?.id ? "Edit billable" : "New billable"}</h3>
        <form onSubmit={save} className="space-y-3" data-testid="billable-form">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Service date</span><input required type="date" value={f.service_date} onChange={upd("service_date")} className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">MBS item #</span><input value={f.mbs_item} onChange={upd("mbs_item")} placeholder="e.g. 80100" className={inputCls} /></label>
            <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-njas-navy">Service description</span><input required value={f.service_description} onChange={upd("service_description")} className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Duration (min)</span><input required type="number" min="0" value={f.duration_minutes} onChange={upd("duration_minutes")} className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Unit amount ($)</span><input type="number" step="0.01" value={f.unit_amount} onChange={upd("unit_amount")} className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Total amount ($)</span><input required type="number" step="0.01" value={f.total_amount} onChange={upd("total_amount")} className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Funding source</span>
              <select value={f.funding_source} onChange={upd("funding_source")} className={inputCls}>
                <option value="medicare">Medicare</option><option value="ndis">NDIS</option><option value="private">Private</option><option value="dva">DVA</option><option value="pro_bono">Pro bono</option><option value="other">Other</option>
              </select>
            </label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Invoice #</span><input value={f.invoice_number} onChange={upd("invoice_number")} className={inputCls} /></label>
            <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-njas-navy">Status</span>
              <select value={f.status} onChange={upd("status")} className={inputCls}>
                <option value="unbilled">Unbilled</option><option value="invoiced">Invoiced</option><option value="paid">Paid</option><option value="written_off">Written off</option>
              </select>
            </label>
          </div>
          <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Notes</span><textarea rows={2} value={f.notes} onChange={upd("notes")} className={inputCls} /></label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm hover:bg-njas-navy-hover disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------- Timeline --------------------
function TimelineTab({ clientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get(`/clients/${clientId}/timeline`).then((r) => setItems(r.data)).finally(() => setLoading(false)); }, [clientId]);
  if (loading) return <div className="text-njas-muted text-sm">Loading…</div>;
  if (items.length === 0) return <div className="text-njas-muted text-sm">Timeline is empty.</div>;
  return (
    <div className="space-y-2">
      {items.map((ev) => (
        <div key={`${ev.type}-${ev.id}`} className="flex gap-3 rounded-lg bg-white border border-[var(--njas-border)] p-3 text-sm">
          <div className="w-16 text-xs text-njas-muted">{ev.at ? new Date(ev.at).toLocaleDateString("en-AU") : "—"}</div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-njas-sage-hover">{ev.type}{ev.contact_type ? ` · ${ev.contact_type}` : ""}{ev.category ? ` · ${ev.category}` : ""}</div>
            <div className="text-njas-text">{ev.summary || ev.filename}</div>
            <div className="text-xs text-njas-muted">{ev.author || ""}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// -------------------- Edit Client --------------------
function EditClientModal({ client, onClose, onSaved }) {
  const [staff, setStaff] = useState([]);
  const [f, set] = useState({
    first_name: client.first_name || "", last_name: client.last_name || "", preferred_name: client.preferred_name || "",
    date_of_birth: client.date_of_birth || "", gender: client.gender || "", pronouns: client.pronouns || "", cultural_identity: client.cultural_identity || "",
    phone: client.phone || "", email: client.email || "", address: client.address || "",
    justice_status: client.justice_status || "", status: client.status || "active", urgency: client.urgency || "standard",
    assigned_worker_id: client.assigned_worker_id || "", background: client.background || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.value });

  useEffect(() => { api.get("/staff/list").then((r) => setStaff(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f };
      Object.keys(body).forEach((k) => { if (body[k] === "") body[k] = null; });
      body.first_name = f.first_name; body.last_name = f.last_name;
      body.status = f.status; body.urgency = f.urgency;
      await api.patch(`/clients/${client.id}`, body);
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-2xl text-njas-navy mb-4">Edit client</h3>
        <form onSubmit={save} className="space-y-4" data-testid="edit-client-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <FL label="First name" req><input required value={f.first_name} onChange={upd("first_name")} className={inputCls} /></FL>
            <FL label="Last name" req><input required value={f.last_name} onChange={upd("last_name")} className={inputCls} /></FL>
            <FL label="Preferred name"><input value={f.preferred_name} onChange={upd("preferred_name")} className={inputCls} /></FL>
            <FL label="Date of birth"><input type="date" value={f.date_of_birth} onChange={upd("date_of_birth")} className={inputCls} /></FL>
            <FL label="Gender"><input value={f.gender} onChange={upd("gender")} className={inputCls} /></FL>
            <FL label="Pronouns"><input value={f.pronouns} onChange={upd("pronouns")} className={inputCls} /></FL>
            <FL label="Cultural identity"><input value={f.cultural_identity} onChange={upd("cultural_identity")} className={inputCls} /></FL>
            <FL label="Phone"><input value={f.phone} onChange={upd("phone")} className={inputCls} /></FL>
            <FL label="Email"><input type="email" value={f.email} onChange={upd("email")} className={inputCls} /></FL>
            <FL label="Address"><input value={f.address} onChange={upd("address")} className={inputCls} /></FL>
            <FL label="Justice status">
              <select value={f.justice_status} onChange={upd("justice_status")} className={inputCls}>
                <option value="">—</option><option value="incarcerated">Incarcerated</option><option value="on_bail">On bail</option>
                <option value="on_parole">On parole</option><option value="on_community_order">On community order</option>
                <option value="released">Released</option><option value="at_risk">At risk</option><option value="family_member">Family member</option><option value="other">Other</option>
              </select>
            </FL>
            <FL label="Assigned worker">
              <select value={f.assigned_worker_id} onChange={upd("assigned_worker_id")} className={inputCls}>
                <option value="">Unassigned</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </FL>
            <FL label="Status">
              <select value={f.status} onChange={upd("status")} className={inputCls}>
                <option value="active">Active</option><option value="on_hold">On hold</option><option value="closed">Closed</option>
              </select>
            </FL>
            <FL label="Urgency">
              <select value={f.urgency} onChange={upd("urgency")} className={inputCls}>
                <option value="low">Low</option><option value="standard">Standard</option><option value="high">High</option><option value="crisis">Crisis</option>
              </select>
            </FL>
          </div>
          <FL label="Background / intake summary"><textarea rows={4} value={f.background} onChange={upd("background")} className={inputCls} /></FL>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm hover:bg-njas-navy-hover disabled:opacity-60" data-testid="edit-client-submit">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FL({ label, req, children }) {
  return <label className="block"><span className="text-xs font-medium text-njas-navy block mb-1.5 uppercase tracking-wider">{label}{req && <span className="text-njas-sage ml-1">*</span>}</span>{children}</label>;
}
